/**
 * Password policy — mirrors backend/app/auth/password_policy.py.
 *
 * Rules (Slice 1 of the auth hardening plan):
 *   - Length 8..128 chars
 *   - At least one letter (unicode-aware)
 *   - At least one digit
 *   - Not equal (case-insensitive) to the username, when known
 *
 * Used on every "set a new password" form: forgot-password reset flow,
 * SignUp, ProfileModal change-password. Login itself never calls this
 * — users typing an existing password shouldn't be re-validated against
 * a policy that may have tightened after their password was set.
 */

export const MIN_PASSWORD_LENGTH = 8;
export const MAX_PASSWORD_LENGTH = 128;

/**
 * Return an error message if the password fails policy, or `null` if it
 * passes. Kept as "return string | null" rather than a boolean so the
 * caller can surface the exact reason without a separate lookup table.
 */
export function validateNewPassword(
  value: string,
  opts: { username?: string } = {},
): string | null {
  if (typeof value !== 'string') return 'Password is required.';
  if (value.length < MIN_PASSWORD_LENGTH) {
    return `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
  }
  if (value.length > MAX_PASSWORD_LENGTH) {
    return `Password must be at most ${MAX_PASSWORD_LENGTH} characters.`;
  }
  // \p{L} = unicode letter; \p{N} = unicode digit. Fall back to ASCII if
  // the runtime doesn't support the /u flag (very old browsers).
  const hasLetter = /\p{L}/u.test(value) || /[A-Za-z]/.test(value);
  const hasDigit = /\p{N}/u.test(value) || /\d/.test(value);
  if (!hasLetter) return 'Password must contain at least one letter.';
  if (!hasDigit) return 'Password must contain at least one digit.';
  if (opts.username && value.trim().toLowerCase() === opts.username.trim().toLowerCase()) {
    return 'Password must not be the same as the username.';
  }
  return null;
}
